import React, { useEffect, useMemo, useState } from "react";
import UsersOrder from "../../cards/UsersOrder";
import useGetWinningAuction from "../../../hooks/orders/useGetWinningAuction";
import useGetLocalWinningAuction from "../../../hooks/orders/useGetLocalWinningAuctions";
import useGetPaidLocalCars from "../../../hooks/orders/useGetPaidLocalCars";
import { ClipLoader } from "react-spinners";
import LocalUsersOrder from "../../cards/LocalUsersOrder";
import Select from "react-select";
import { FiSearch } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";

const Order = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("main");
  const [searchTerm, setSearchTerm] = useState("");
  const [mainStatusFilter, setMainStatusFilter] = useState(null);
  const [localStatusFilter, setLocalStatusFilter] = useState(null);
  const [localPaidType, setLocalPaidType] = useState({ value: "not_paid", label: "Not Paid" });
  const [needsRefresh, setNeedsRefresh] = useState(false);
  
  const { orders, loading, error, fetchOrders } = useGetWinningAuction();
  const { localAuction, localLoading, localError, fetchLocalWinningAuctions } =
    useGetLocalWinningAuction();
  const { paidLocal, fetchPaidLocalCars } = useGetPaidLocalCars();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Rehydrate state from URL (path + query)
  useEffect(() => {
    const isLocalPath = location.pathname.endsWith("/local");
    const tabParam = queryParams.get("tab");
    const nextTab = isLocalPath || tabParam === "local" ? "local" : "main";
    setActiveTab(nextTab);

    const q = queryParams.get("q");
    if (q !== null) {
      setSearchTerm(q);
    }

    const mainStatus = queryParams.get("mainStatus");
    if (mainStatus !== null) {
      setMainStatusFilter(mainStatus ? { value: mainStatus, label: mainStatus || "All Statuses" } : null);
    }

    const localStatus = queryParams.get("localStatus");
    if (localStatus !== null) {
      setLocalStatusFilter(localStatus ? { value: localStatus, label: localStatus || "All Statuses" } : null);
    }

    const paid = queryParams.get("paid");
    if (paid === "paid" || paid === "not_paid") {
      setLocalPaidType({ value: paid, label: paid === "paid" ? "Paid" : "Not Paid" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Sync state to URL for persistence/deep linking
  const updateUrl = (nextPartial) => {
    const params = new URLSearchParams(location.search);
    const next = {
      tab: activeTab,
      q: searchTerm,
      mainStatus: mainStatusFilter?.value ?? "",
      localStatus: localStatusFilter?.value ?? "",
      paid: localPaidType?.value ?? "not_paid",
      ...nextPartial
    };

    if (next.q) params.set("q", next.q); else params.delete("q");
    if (next.tab && next.tab !== "main") params.set("tab", next.tab); else params.delete("tab");
    if (next.mainStatus) params.set("mainStatus", next.mainStatus); else params.delete("mainStatus");
    if (next.localStatus) params.set("localStatus", next.localStatus); else params.delete("localStatus");
    if (next.tab === "local") {
      if (next.paid) params.set("paid", next.paid); else params.delete("paid");
    } else {
      params.delete("paid");
    }

    const basePath = "/user/account/order";
    const pathname = next.tab === "local" ? `${basePath}/local` : basePath;
    navigate({ pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
  };
  
  useEffect(() => {
    if (activeTab === "main") {
      fetchOrders();
    } else {
      fetchLocalWinningAuctions();
      fetchPaidLocalCars();
    }
    setNeedsRefresh(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, needsRefresh]);

  const userOrders = orders?.data?.data;
  const localUserOrders = localAuction?.data?.data;
  const paidLocalOrders = paidLocal?.data?.data;

  // Status options for main car orders
  const mainStatusOptions = [
    { value: "", label: "All Statuses" },
    { value: "Pending", label: "Pending" },
    { value: "Processing", label: "Processing" },
    { value: "Shipped", label: "Shipped" },
    { value: "Delivered", label: "Delivered" },
    { value: "Complete", label: "Completed" },
    { value: "Cancelled", label: "Cancelled" },
  ];

  // Status options for local car orders
  const localStatusOptions = [
    { value: "", label: "All Statuses" },
    { value: "Sold On Approval", label: "On Reserve" },
    { value: "Sold", label: "Won" },
  ];

  // Filter functions for both order types
  const filterMainOrders = () => {
    if (!userOrders) return [];
    
    return userOrders.filter(order => {
      const titleMatch = order.title?.toLowerCase().includes(searchTerm.toLowerCase());
      const statusMatch = !mainStatusFilter?.value || order.orderStatus === mainStatusFilter.value;
      
      return titleMatch && statusMatch;
    });
  };

  const filterLocalOrders = () => {
    if (!localUserOrders) return [];
    
    return localUserOrders.filter(order => {
      const titleMatch = order.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         order.carTitle?.toLowerCase().includes(searchTerm.toLowerCase());
      const statusMatch = !localStatusFilter?.value || 
                          order.status === localStatusFilter.value;
      return titleMatch && statusMatch;
    });
  };

  const filterPaidLocalOrders = () => {
    if (!paidLocalOrders) return [];
    return paidLocalOrders.filter(order => {
      const titleMatch = order.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         order.carTitle?.toLowerCase().includes(searchTerm.toLowerCase());
      const statusMatch = !localStatusFilter?.value || 
                          order.status === localStatusFilter.value;
      return titleMatch && statusMatch;
    });
  };

  // Custom styles for React Select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '40px',
      borderRadius: '0.5rem',
      border: '1px solid #e2e8f0',
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #cbd5e0',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#E53E3E' : state.isFocused ? '#FED7D7' : null,
      color: state.isSelected ? 'white' : 'black',
    }),
  };

  // Function to render main car orders tab content
  const renderMainTabContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-[50vh]">
          <ClipLoader />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <p className="text-2xl font-bold text-gray-500">{error}</p>
          <p className="text-gray-400 mt-2">Please try again later</p>
        </div>
      );
    }

    if (!userOrders) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <p className="text-2xl font-bold text-gray-500">No data available</p>
          <p className="text-gray-400 mt-2">Please refresh the page</p>
        </div>
      );
    }

    const filteredOrders = filterMainOrders();

    return filteredOrders.length > 0 ? (
      <UsersOrder userOrders={filteredOrders} />
    ) : (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <p className="text-2xl font-bold text-gray-500">
          No matching orders found
        </p>
        <p className="text-gray-400 mt-2">
          Try adjusting your search or filter criteria
        </p>
      </div>
    );
  };

  // Function to render local car orders tab content
  const renderLocalTabContent = ( needsRefresh, setNeedsRefresh ) => {
    if (localLoading) {
      return (
        <div className="flex justify-center items-center h-[50vh]">
          <ClipLoader />
        </div>
      );
    }

    if (localError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <p className="text-2xl font-bold text-gray-500">{localError}</p>
          <p className="text-gray-400 mt-2">Please try again later</p>
        </div>
      );
    }

    if (!localUserOrders || localUserOrders.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <p className="text-2xl font-bold text-gray-500">No data available</p>
          <p className="text-gray-400 mt-2">Please refresh the page</p>
        </div>
      );
    }

    const filteredNotPaid = filterLocalOrders();
    const filteredPaid = filterPaidLocalOrders();
    const showPaid = localPaidType?.value === "paid";

    return (showPaid ? filteredPaid.length > 0 : filteredNotPaid.length > 0) ? (
      <LocalUsersOrder
        userOrders={showPaid ? [] : filteredNotPaid}
        paidOrders={showPaid ? (filteredPaid || []) : []}
        needsRefresh={needsRefresh}
        setNeedsRefresh={setNeedsRefresh}
      />
    ) : (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <p className="text-2xl font-bold text-gray-500">
          No matching local car orders found
        </p>
        <p className="text-gray-400 mt-2">
          Try adjusting your search or filter criteria
        </p>
      </div>
    );
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    updateUrl({ q: value });
  };

  // Handle status filter change for main car orders
  const handleMainStatusChange = (selectedOption) => {
    setMainStatusFilter(selectedOption);
    updateUrl({ mainStatus: selectedOption?.value ?? "" });
  };

  // Handle status filter change for local car orders
  const handleLocalStatusChange = (selectedOption) => {
    setLocalStatusFilter(selectedOption);
    updateUrl({ localStatus: selectedOption?.value ?? "" });
  };

  // Render the appropriate filter based on active tab
  const renderStatusFilter = () => {
    if (activeTab === "main") {
      return (
        <Select
          options={mainStatusOptions}
          styles={customSelectStyles}
          placeholder="Filter by status"
          isClearable
          onChange={handleMainStatusChange}
          value={mainStatusFilter}
          className="text-sm"
        />
      );
    } else {
      return (
        <div className="flex gap-4 w-full md:w-auto">
          <Select
            options={localStatusOptions}
            styles={customSelectStyles}
            placeholder="Filter by status"
            isClearable
            onChange={handleLocalStatusChange}
            value={localStatusFilter}
            className="text-sm min-w-[200px]"
          />
          <Select
            options={[{ value: "not_paid", label: "Not Paid" }, { value: "paid", label: "Paid" }]}
            styles={customSelectStyles}
            placeholder="Paid type"
            onChange={(opt) => { setLocalPaidType(opt); updateUrl({ paid: opt?.value ?? "not_paid", tab: "local" }); }}
            value={localPaidType}
            className="text-sm min-w-[140px]"
          />
        </div>
      );
    }
  };

  return (
    <>
      <div className="w-[90%] lg:w-[85vw] mx-auto mt-6 lg:mt-[1.5vw] text-black">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h1 className="text-[24px] md:text-[36px] lg:text-[2.3vw] text-left font-urbanist lg:text-left font-bold mb-4">
            Winning Auctions
          </h1>

          <div className="flex justify-end lg:justify-start space-x-4 mb-3 lg:mb-[1.5vw]">
            <button
              className={`px-[1vw] py-[0.5vw] lg:text-[1vw] font-semibold rounded-md lg:rounded-[0.25vw] ${activeTab === "main" ? "bg-primary-red text-white" : "bg-gray-200 text-gray-600"}`}
              onClick={() => { setActiveTab("main"); updateUrl({ tab: "main" }); }}
            >
              Copart/IAAI Cars
            </button>
            <button
              className={`px-[1vw] py-[0.5vw] lg:text-[1vw] font-semibold rounded-md lg:rounded-[0.25vw] ${activeTab === "local" ? "bg-primary-red text-white" : "bg-gray-200 text-gray-600"}`}
              onClick={() => { setActiveTab("local"); updateUrl({ tab: "local" }); }}
            >
              Local Cars
            </button>
          </div>
        </div>

        {/* Search and filter section */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-stretch md:items-center mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <FiSearch className="text-gray-500" />
            </div>
            <input
              type="text"
              className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-primary-red focus:border-primary-red"
              placeholder="Search by car title..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div className="w-full md:w-auto flex justify-start md:justify-end">
            {renderStatusFilter()}
          </div>
        </div>

        <div className="">
          {activeTab === "main"
            ? renderMainTabContent()
            : renderLocalTabContent(needsRefresh, setNeedsRefresh,localUserOrders)}
        </div>
      </div>
    </>
  );
};

export default Order;
